import axios from 'axios';
import { 
  LopHocPhan,
  LopHocPhanListParams, 
  LopHocPhanListResponse,
  LopHocPhanCreateRequest,
  LopHocPhanUpdateRequest,
  LopHocPhanSyncRequest,
  LopHocPhanSyncResponse,
  LopHocPhanImportRequest,
  LopHocPhanImportResponse,
  ApiResponse 
} from '../../../shared/types';

const API_BASE = '/api/v1/lophocphan';

class LopHocPhanService {
  // Get LopHocPhan list with pagination, search, and filters
  async getLopHocPhanList(params: LopHocPhanListParams): Promise<LopHocPhanListResponse> {
    try {
      // Use new API with proper query params
      const response = await axios.get(API_BASE, {
        params: {
          page: params.page || 1,
          limit: params.limit || 10000, // Large limit to get all data for grouping
          search: params.search || undefined,
          khoaHocId: params.khoaHocId || undefined,
          monHocId: params.monHocId || undefined
        }
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Get LopHocPhan by ID
  async getLopHocPhanById(id: string): Promise<ApiResponse<LopHocPhan>> {
    const response = await axios.get<ApiResponse<LopHocPhan>>(`${API_BASE}/${id}`);
    return response.data;
  }

  // Get LopHocPhan by MonHocID
  async getLopHocPhanByMonHoc(monHocId: string): Promise<ApiResponse<LopHocPhan[]>> {
    const response = await axios.get<ApiResponse<LopHocPhan[]>>(`${API_BASE}/by-monhoc/${monHocId}`);
    return response.data;
  }

  // Get LopHocPhan and NhomThucHanh options for dropdown
  async getLopHocPhanOptions(khoaHocId: string, monHocId: string): Promise<ApiResponse<any>> {
    const response = await axios.get<ApiResponse<any>>(`${API_BASE}/options`, {
      params: { khoaHocId, monHocId }
    });
    return response.data;
  }

  // Create LopHocPhan
  async createLopHocPhan(data: LopHocPhanCreateRequest): Promise<ApiResponse<void>> {
    const response = await axios.post<ApiResponse<void>>(API_BASE, data);
    return response.data;
  }

  // Update LopHocPhan
  async updateLopHocPhan(id: string, data: LopHocPhanUpdateRequest): Promise<ApiResponse<void>> {
    const response = await axios.put<ApiResponse<void>>(`${API_BASE}/${id}`, data);
    return response.data;
  }

  // Delete LopHocPhan
  async deleteLopHocPhan(maLHP: string): Promise<ApiResponse> {
    const response = await axios.delete<ApiResponse>(`${API_BASE}/${maLHP}`);
    return response.data;
  }

  // Sync LopHocPhan from secondary database
  async syncLopHocPhan(data: LopHocPhanSyncRequest): Promise<LopHocPhanSyncResponse> {
    const response = await axios.post<LopHocPhanSyncResponse>(`${API_BASE}/sync`, data);
    return response.data;
  }

  // Sync all LopHocPhan by KhoaHoc
  async syncAllLopHocPhan(khoaHocId: string): Promise<LopHocPhanSyncResponse> {
    const response = await axios.post<LopHocPhanSyncResponse>(`${API_BASE}/sync-all`, { KhoaHocID: khoaHocId });
    return response.data;
  }

  // Import LopHocPhan from Excel
  async importLopHocPhan(data: LopHocPhanImportRequest): Promise<LopHocPhanImportResponse> {
    const response = await axios.post<LopHocPhanImportResponse>(`${API_BASE}/import`, data);
    return response.data;
  }

  // Export LopHocPhan to Excel template
  async exportTemplate(): Promise<Blob> {
    const response = await axios.get(`${API_BASE}/template`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ============== STUDENT MANAGEMENT ==============
  
  // Get students in LopHocPhan
  async getSinhVienInLopHocPhan(lopHocPhanId: string): Promise<ApiResponse<any[]>> {
    const response = await axios.get<ApiResponse<any[]>>(`${API_BASE}/${lopHocPhanId}/sinhvien`);
    return response.data;
  }

  // Get students with full info in LopHocPhan
  async getSinhVienFullInfo(lopHocPhanId: string): Promise<ApiResponse<any[]>> {
    const response = await axios.get<ApiResponse<any[]>>(`${API_BASE}/${lopHocPhanId}/sinhvien/full`);
    return response.data;
  }

  // Add students to LopHocPhan (replace existing)
  async addSinhVienToLopHocPhan(lopHocPhanId: string, maHSList: string[]): Promise<ApiResponse<void>> {
    const response = await axios.post<ApiResponse<void>>(`${API_BASE}/${lopHocPhanId}/sinhvien`, { maHSList });
    return response.data;
  }

  // Remove a student from LopHocPhan
  async removeSinhVienFromLopHocPhan(lopHocPhanId: string, maHS: string): Promise<ApiResponse<void>> {
    const response = await axios.delete<ApiResponse<void>>(`${API_BASE}/${lopHocPhanId}/sinhvien/${maHS}`);
    return response.data;
  }

  // ============== PRACTICE GROUP MANAGEMENT ==============
  
  // Get NhomThucHanh by LopHocPhanID
  async getNhomThucHanhByLopHocPhan(lopHocPhanId: string): Promise<ApiResponse<any[]>> {
    const response = await axios.get<ApiResponse<any[]>>(`${API_BASE}/${lopHocPhanId}/nhomthuchanh`);
    return response.data;
  }

  // Create NhomThucHanh (divide students into groups)
  async createNhomThucHanh(data: { LopHocPhanID: string; SoNhom: number; SinhViens: any[] }): Promise<ApiResponse<any>> {
    const response = await axios.post<ApiResponse<any>>(`${API_BASE}/nhomthuchanh/create`, data);
    return response.data;
  }

  // Delete all NhomThucHanh for a LopHocPhan
  async deleteNhomThucHanh(lopHocPhanId: string): Promise<ApiResponse<void>> {
    const response = await axios.delete<ApiResponse<void>>(`${API_BASE}/${lopHocPhanId}/nhomthuchanh`);
    return response.data;
  }

  async getStudentsInClassSection(lopHocPhanId: string): Promise<ApiResponse<any[]>> {
    const response = await axios.get<ApiResponse<any[]>>(`/api/v1/lophocphan-sinhvien/${lopHocPhanId}/sinhvien/full`);
    return response.data;
  }

  async updateStudentsInClassSection(lopHocPhanId: string, maHSList: string[]): Promise<ApiResponse> {
    const response = await axios.put<ApiResponse>(`/api/v1/lophocphan-sinhvien/${lopHocPhanId}/sinhvien`, { maHSList });
    return response.data;
  }
}

export default new LopHocPhanService(); 
